import { Paper, Stack, useTheme } from "@carrotfertility/carotene-core"
import { Skeleton } from "@internal/ui"
import React from "react"
import { perViewport } from "./responsive"

/**
 * The Appointments page's loading state: skeletons mirroring the fullest common frame (Up next card,
 * Past list, booking card) so the layout doesn't jump. No design frame exists for this state yet.
 */

const ROW_AVATAR_SIZE = perViewport(48, 54)

/** A provider/visit row: avatar + two lines of text, the shape both Up next and Past rows share. */
function SkeletonRow(): JSX.Element {
  const theme = useTheme()
  return (
    <Stack sx={{ flexDirection: "row", alignItems: "center" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
      <Skeleton circular sx={{ inlineSize: ROW_AVATAR_SIZE, blockSize: ROW_AVATAR_SIZE }} />
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.xs)}>
        <Skeleton width={168} height={16} />
        <Skeleton width={216} height={14} />
      </Stack>
    </Stack>
  )
}

function SkeletonCard({ children }: { children: React.ReactNode }): JSX.Element {
  const theme = useTheme()
  return (
    <Paper
      elevation={0}
      sx={{
        padding: theme.spacing(theme.tokens.spacing.lg),
        borderRadius: theme.tokens.borderRadius.md
      }}
    >
      {children}
    </Paper>
  )
}

export function AppointmentsLoading(): JSX.Element {
  const theme = useTheme()
  return (
    <Stack
      role="status"
      aria-label="Loading your appointments"
      sx={{ flexDirection: "column" }}
      spacing={theme.spacing(theme.tokens.spacing.xl)}
    >
      {/* Up next */}
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Skeleton width={96} height={24} />
        <SkeletonCard>
          <SkeletonRow />
          <Skeleton
            width={128}
            height={44}
            sx={{
              marginBlockStart: theme.spacing(theme.tokens.spacing.lg),
              borderRadius: theme.tokens.borderRadius.md
            }}
          />
        </SkeletonCard>
      </Stack>

      {/* Past */}
      <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
        <Skeleton width={64} height={20} />
        <SkeletonRow />
        <SkeletonRow />
      </Stack>

      {/* Book appointment */}
      <SkeletonCard>
        <Stack sx={{ flexDirection: "row" }} spacing={theme.spacing(theme.tokens.spacing.xs)}>
          <Skeleton circular width={40} height={40} />
          <Skeleton circular width={40} height={40} />
          <Skeleton circular width={40} height={40} />
        </Stack>
        <Skeleton width={220} height={18} sx={{ marginBlockStart: theme.spacing(theme.tokens.spacing.md) }} />
        <Skeleton width="70%" height={14} sx={{ marginBlockStart: theme.spacing(theme.tokens.spacing.sm) }} />
        <Skeleton
          width={160}
          height={44}
          sx={{
            marginBlockStart: theme.spacing(theme.tokens.spacing.lg),
            borderRadius: theme.tokens.borderRadius.md
          }}
        />
      </SkeletonCard>
    </Stack>
  )
}
